import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorIcon from '@mui/icons-material/Error';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Toast } from '../../components';
import DeleteIcon from 'app/components/Svgs/DeleteIcon';
import { LanguageSwitcher, StyledButton, Loader, Alert } from 'app/components/v2';
import {
  MainContainer,
  Container,
  SubTitle,
  SubTitleText,
  ParentGrid,
  ChildGrid,
  SubConatiner,
  SubConatiner2,
  SubConatiner3,
  SubTitle2,
  ContainerStack,
  InputContainerStack,
  CustomAdornment,
  TitleContainer,
  CustomAdornment2,
} from '../../layout/style';
import { toggleReset } from 'app/store/features/notificationsSlice';
import { setAlertOpen } from 'app/store/features/toggleSidebarSlice';
import { DarkmodeColors, ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';
import appService from 'app/services/appService';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { setUser } from 'app/store/features/updateUserSlice';

function Settings() {
  const [open, setOpen] = useState(false);
  const [deleteAccount, setDeleteAccount] = useState(false);
  const [roles, setRoles] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();

  const accSettings = useSelector((state) => state.updateUser.userData)

  const { t } = useTranslation('translation', { keyPrefix: 'settings_headings' });

  const phoneNumberAutoFormat = phoneNumber => {
    const number = phoneNumber.trim().replace(/[^{+}0-9]/g, '');

    if (number.length < 8) return number;
    return number.replace(/(\d{11})(\d{10})/, '$1$2');
  };

  const onChange = e => {
    const targetValue = phoneNumberAutoFormat(e.target.value);
    return targetValue;
  };

  const fetchRolesAndAuthorities = async () => {
    setLoader(true);
    try {
      const [rolesRes, authoritiesRes] = await Promise.all([
        apiService.getRoles(),
        apiService.getAuthorities(),
      ]);
      setRoles(rolesRes.data);
      setAuthorities(authoritiesRes.data);
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchRolesAndAuthorities();
    dispatch(toggleReset());
  }, []);

  const saveProfile = async newUserData => {
    try {
      const res = await apiService.setProfile(newUserData);
      return res;
    } catch (error) {
      console.error('Failed to save profile:', error);
      return null;
    }
  };
  
  const handleFormSubmit = async values => {
    const saveProfileRes = await saveProfile(values);
    if (saveProfileRes) {
      try {
        const getProfileRes = await apiService.getProfile();
        dispatch(setUser(getProfileRes.data.data));
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }
    }
    setOpen(true);
  };
  

  const profileSchema = yup.object().shape({
    firstName: yup.string().required(t('error_fname')),
    lastName: yup.string().required(t('error_lname')),
    email: yup.string().email(t('email_invalid')).required(t('error_email')),
    phoneNr: yup.string().min(9).required(t('error_phoneNr')),
    persNr: yup.string().min(12).required(t('error_persNr')),
    role: yup.string().required(t('error_role')),
    auth: yup.string().required(t('error_auth')),
  });
  const { values, errors, handleChange, handleBlur, touched, handleSubmit, setFieldValue } =
    useFormik({
      enableReinitialize: true,
      initialValues: {
        firstName: accSettings.firstName,
        lastName: accSettings.lastName,
        email: accSettings.email,
        phoneNr: accSettings.phoneNr,
        persNr: accSettings.persNr,
        role: accSettings.role,
        auth: accSettings.auth,
      },
      validationSchema: profileSchema,
      onSubmit(values) {
        handleFormSubmit(values);
      },
    });

  const deleteCurrentAccount = async () => {
    await apiService.deleteUser(accSettings.id);
    appService.logout();
  };

  const handleDisable = () => {
    if (
      accSettings.firstName === values.firstName &&
      accSettings.lastName === values.lastName &&
      accSettings.email === values.email &&
      accSettings.phoneNr === values.phoneNr
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (
      accSettings.firstName !== values.firstName ||
      accSettings.lastName !== values.lastName ||
      accSettings.email !== values.email ||
      accSettings.phoneNr !== values.phoneNr
    ) {
      dispatch(setAlertOpen(true));
    } else {
      dispatch(setAlertOpen(false));
    }
  }, [values]);

  return (
      <MainContainer>
        <TitleContainer paddingBottom={1}>
          <Typography variant='h4' sx={{ color: ColorsV2.white }}>{t('main')}</Typography>
          <StyledButton
            name='IconButton'
            sx={{ fontSIze: '0.7em', fontWeight: 'light' }}
            onClick={handleSubmit}
            disabled={handleDisable()}
            startIcon={<SettingsOutlinedIcon sx={{ fontSize: '0.7em' }} fill={ColorsV2.white} />}
          >
            {t('button_text')}
          </StyledButton>
        </TitleContainer>
        <Divider sx={{ borderColor: ColorsV2.outline }} />
        {!loader && (
          <Container
            sx={{
              backgroundColor: ColorsV2.primary,
            }}
            elevation={0}
          >
            <ParentGrid container>
              <ChildGrid item>
                <SubConatiner sx={{ borderBottom: `1px solid ${ColorsV2.white}` }}>
                  <SubTitle gutterBottom sx={{ color: ColorsV2.white }}>
                    {t('sub_1')}
                  </SubTitle>
                  <SubTitleText sx={{ color: ColorsV2.white }}>{t('desc_1')}</SubTitleText>
                </SubConatiner>
                <ContainerStack
                  direction='row'
                  sx={{ borderBottom: `1px solid ${ColorsV2.white}` }}
                >
                  <SubConatiner2 border='none'>
                    <SubTitle2 gutterBottom sx={{ color: ColorsV2.white }}>
                      {t('sub_1_1')}
                    </SubTitle2>
                    <SubTitleText sx={{ color: ColorsV2.white }}>{t('desc_1_1')}</SubTitleText>
                  </SubConatiner2>
                  <LanguageSwitcher loggedIn={true} />
                </ContainerStack>
                {/* {!V2 && (
                  <ContainerStack
                    direction='row'
                    sx={{ borderBottom: `1px solid ${ColorsV2.white}` }}
                  >
                    <SubConatiner2 border='none'>
                      <SubTitle2 gutterBottom sx={{ color: ColorsV2.white }}>
                        {t('sub_1_2')}
                      </SubTitle2>
                      <SubTitleText gutterBottom sx={{ color: ColorsV2.white }}>
                        {t('desc_1_2')}
                      </SubTitleText>
                    </SubConatiner2>
                    <SelectConatiner>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.darkmode}
                            handlechange={handleTheme}
                            name='darkmode'
                          />
                        }
                      />
                    </SelectConatiner>
                  </ContainerStack>
                )} */}
                {/* <ContainerStack
                  direction='row'
                  sx={{ borderBottom: `1px solid ${DarkmodeColors.menuIcon}` }}
                >
                  <SubConatiner2 border='none'>
                    <SubTitle2 gutterBottom sx={{ color: ColorsV2.white }}>
                      {t('switch')}
                    </SubTitle2>
                    <SubTitleText gutterBottom sx={{ color: ColorsV2.white }}>
                      {t('set_version')}
                    </SubTitleText>
                  </SubConatiner2>
                  <SelectConatiner>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={V2}
                          handlechange={() => dispatch(setVersion2(!V2))}
                          name='version-control'
                        />
                      }
                    />
                  </SelectConatiner>
                </ContainerStack> */}
              </ChildGrid>
              <ChildGrid item>
                <SubConatiner3>
                  <SubTitle gutterBottom sx={{ color: 'white' }}>
                    {t('sub_2')}
                  </SubTitle>
                  <SubTitleText sx={{ color: 'white' }}>{t('desc_2')}</SubTitleText>
                </SubConatiner3>
                <form style={{ width: '100%' }}>
                  <Stack sx={{ width: '100%' }}>
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                      }}
                    >
                      <TextField
                        required
                        size='small'
                        name='firstName'
                        // label={t('firstname')}
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.firstName && errors.firstName}
                        sx={{
                          marginBottom: '15px',
                          width: '49%',
                          backgroundColor: 'transparent',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: ColorsV2.white,
                          },
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: ColorsV2.white,
                            },
                          },
                          height: '77%',
                          marginRight: '8px',
                          '&  .MuiFormHelperText-root.Mui-error': {
                            background: ColorsV2.primary,
                            color: '#FF2800',
                            width: '101%',
                            height: '100%',
                            fontSize: '0.7em',
                            marginTop: '-0.5%',
                            marginLeft: -0.15,
                          },
                          '&:-webkit-autofill,&:-webkit-autofill:hover,&:-webkit-autofill:focus,& input:-internal-autofill-selected':
                            {
                              backgroundColor: 'transparent !important',
                            },
                        }}
                        InputProps={{
                          startAdornment: (
                            <CustomAdornment2 position='start' sx={{ color: '#8F95B2' }}>
                              {t('firstname')}:
                            </CustomAdornment2>
                          ),
                          style: {
                            color: ColorsV2.white,
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.7em',
                            fontWeight: '400',
                            lineHeight: '21px',
                            borderRadius: '8px',
                            height: '3.5em',
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            color: errors.firstName ? ColorsV2.white : 'inherit',
                          },
                        }}
                      />

                      <TextField
                        required
                        size='small'
                        name='lastName'
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.lastName && errors.lastName}
                        InputProps={{
                          startAdornment: (
                            <CustomAdornment2 position='start' sx={{ color: '#8F95B2' }}>
                              {t('lastname')}:
                            </CustomAdornment2>
                          ),
                          style: {
                            color: ColorsV2.white,
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.7em',
                            fontWeight: '400',
                            lineHeight: '21px',
                            borderRadius: '8px',
                            height: '3.5em',
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            color: errors.lastName ? ColorsV2.white : 'inherit',
                          },
                        }}
                        sx={{
                          width: '49%',
                          backgroundColor: 'transparent',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: ColorsV2.white,
                          },
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: ColorsV2.white,
                            },
                          },
                          height: '77%',
                          '&  .MuiFormHelperText-root.Mui-error': {
                            background: ColorsV2.primary,
                            color: '#FF2800',
                            width: '101%',
                            height: '100%',
                            fontSize: '0.7em',
                            marginTop: '-0.5%',
                            marginLeft: -0.15,
                          },
                        }}
                      />
                    </div>
                  </Stack>
                </form>

                <InputContainerStack sx={{ borderBottom: `1px solid ${ColorsV2.white}` }}>
                  <TextField
                    required
                    size='small'
                    name='email'
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && errors.email}
                    sx={{
                      marginBottom: '15px',
                      backgroundColor: 'transparent',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: ColorsV2.white,
                      },
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: ColorsV2.white,
                        },
                      },
                      '&  .MuiFormHelperText-root.Mui-error': {
                        background: ColorsV2.primary,
                        color: '#FF2800',
                        width: '101%',
                        height: '100%',
                        fontSize: '0.7em',
                        marginTop: '-0.1%',
                        marginLeft: -0.15,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <CustomAdornment2 position='start' sx={{ color: '#8F95B2' }}>
                          {t('email')}:
                        </CustomAdornment2>
                      ),
                      style: {
                        color: ColorsV2.white,
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '0.7em',
                        fontWeight: '400',
                        lineHeight: '21px',
                        borderRadius: '8px',
                        height: '3.5em',
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        color: errors.email ? ColorsV2.white : 'inherit',
                      },
                    }}
                  />{' '}
                  <TextField
                    type='tel'
                    size='small'
                    fullWidth
                    name='phoneNr'
                    value={values.phoneNr}
                    onChange={e => setFieldValue('phoneNr', onChange(e))}
                    onBlur={handleBlur}
                    error={touched.phoneNr && errors.phoneNr}
                    helperText={
                      touched.phoneNr && errors.phoneNr ? (
                        <>
                          <ErrorIcon sx={{ mr: 1, fontSize: '0.8em', verticalAlign: 'middle' }} />
                          {errors.phoneNr}
                        </>
                      ) : null
                    }
                    InputProps={{
                      startAdornment: (
                        <CustomAdornment2 position='start' sx={{ color: '#8F95B2' }}>
                          {t('phone')}:
                        </CustomAdornment2>
                      ),
                      style: {
                        color: ColorsV2.white,
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '0.7em',
                        fontWeight: '400',
                        lineHeight: '21px',
                        borderRadius: '8px',
                        height: '3.5em',
                      },
                    }}
                    InputLabelProps={{
                      style: {
                        color: errors.firstName ? ColorsV2.white : 'inherit',
                      },
                    }}
                    inputProps={{
                      maxLength: 14,
                      style: {
                        color: ColorsV2.white,
                      },
                    }}
                    sx={{
                      backgroundColor: 'transparent',

                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: ColorsV2.white,
                      },
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: ColorsV2.white,
                        },
                      },
                      // backgroundColor: '#778899',
                      '& label.Mui-focused': {
                        color: ColorsV2.primary,
                        transform: 'translate(15px, -16px)',
                        fontSize: '0.75rem',
                      },
                      '&  .MuiFormHelperText-root.Mui-error': {
                        background: ColorsV2.primary,
                        color: '#FF2800',
                        width: '101%',
                        height: '100%',
                        fontSize: '0.7em',
                        marginTop: '-0.1%',
                        marginLeft: -0.15,
                      },
                      borderRadius: '8px',
                      marginBottom: '15px',
                    }}
                  />
                  <TextField
                    required
                    size='small'
                    name='persNr'
                    disabled
                    sx={{
                      marginBottom: '10px',
                      backgroundColor: 'transparent',
                      border: ColorsV2.white,
                      outline: 'none',
                      borderRadius: '4px',
                      '& .MuiInputBase-root.Mui-disabled': {
                        backgroundColor: '#92929D',
                      },
                    }}
                    InputProps={{
                      maxLength: 12,
                      startAdornment: (
                        <CustomAdornment position='start' sx={{ color: 'white' }}>
                          {t('personal')}: {values.persNr}
                        </CustomAdornment>
                      ),
                      style: {
                        color: ColorsV2.white,
                        borderRadius: '8px',
                        height: '2.5em',
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        color: errors.persNr ? ColorsV2.white : 'inherit',
                      },
                    }}
                  />{' '}
                </InputContainerStack>
                <SubConatiner3>
                  <SubTitle gutterBottom sx={{ color: ColorsV2.white }}>
                    {t('sub_3')}
                  </SubTitle>
                  <SubTitleText sx={{ color: ColorsV2.white }}>{t('desc_3')}</SubTitleText>
                </SubConatiner3>
                <Box width='100%'>
                  <TextField
                    select
                    fullWidth
                    size='small'
                    disabled
                    value=""
                    sx={{
                      marginBottom: '5px',
                      marginTop: '6px',
                      backgroundColor: 'transparent',
                      border: DarkmodeColors.menuIcon,
                      outline: 'none',
                      borderRadius: '4px',
                      '& .MuiInputBase-root.Mui-disabled': {
                        backgroundColor: '#92929D',
                      },
                    }}
                    name='role'
                    InputProps={{
                      startAdornment: (
                        <CustomAdornment position='start' sx={{ color: 'white' }}>
                          {t('role')}:{' '}
                          {roles.find(role => values?.role === role.id)?.name ||
                            t('role_not_found')}
                        </CustomAdornment>
                      ),
                      style: {
                        borderRadius: '8px',
                        height: '2.5em',
                      },
                    }}
                  >
                    <MenuItem>No selection available</MenuItem>
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size='small'
                    disabled
                    value=""
                    sx={{
                      marginTop: '10px',
                      marginBottom: '40px',
                      backgroundColor: 'transparent',
                      border: DarkmodeColors.menuIcon,
                      outline: 'none',
                      borderRadius: '4px',
                      '& .MuiInputBase-root.Mui-disabled': {
                        backgroundColor: '#92929D',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <CustomAdornment position='start' sx={{ color: 'white' }}>
                          {t('permission')}:{' '}
                          {authorities.find(authority => values?.auth === authority?.id)?.name ||
                            ''}
                        </CustomAdornment>
                      ),
                      style: {
                        borderRadius: '8px',
                        height: '2.5em',
                      },
                    }}
                  >
                    <MenuItem>No selection available</MenuItem>
                  </TextField>
                </Box>
                <StyledButton
                  name='IconButton'
                  onClick={() => setDeleteAccount(true)}
                  sx={{
                    color: ColorsV2.white,
                    fontSize: '0.8em',
                    fontWeight: 'light',
                  }}
                  startIcon={<DeleteIcon fill={ColorsV2.white} fontSize={'0.9em'} />}
                >
                  {t('delete')}
                </StyledButton>
              </ChildGrid>
            </ParentGrid>
          </Container>
        )}
        {loader && <Loader />}
        <Toast
          open={open}
          setOpen={setOpen}
          message='Profile updated Sucessfully'
          severity='success'
        />
        <Alert
          title='Alert'
          text='Are you sure want to delete your account'
          open={deleteAccount}
          setOpen={setDeleteAccount}
          handleConfirmation={deleteCurrentAccount}
          type='Delete'
        />
        {/* <RefreshTokenPopup /> */}
      </MainContainer>
  );
}

export default Settings;
